import React, { FC, useCallback, useMemo } from 'react';

import { EvSection, EvStackedBarChart } from '@evinced-private/ui-common';

import { createStrategicTickPositions } from '../../../../common/helpers/chartHelpers';
import { ERROR_TYPE } from '../../types/chartErrors';
import { ErrorView } from '../error-view/ErrorView';
import { ChartSkeleton } from '../skeletons/chart-skeleton/ChartSkeleton';

import { createTooltipContent } from './helpers/tooltipHelpers';
import { useChartData } from './hooks/useChartData';
import { useDataToggle } from './hooks/useDataToggle';
import { CHART_CONSTANTS, CHART_TEXT } from './constants';
import { AccessibilityCriticalIssuesStackedChartProps } from './types';

import './AccessibilityCriticalIssuesStackedChart.scss';

export const AccessibilityCriticalIssuesStackedChart: FC<
	AccessibilityCriticalIssuesStackedChartProps
> = ({
	data,
	uniqueIssuesData,
	isLoading = false,
	error = null,
	title = CHART_TEXT.TITLE,
	height = CHART_CONSTANTS.HEIGHT,
	className = '',
	showTable = true
}) => {
	const {
		showUnique,
		setShowUnique,
		hasError: toggleHasError,
		canToggle
	} = useDataToggle(data, uniqueIssuesData);
	const { chartData, patterns, categories } = useChartData(data, uniqueIssuesData, showUnique);

	const hasError = useMemo(() => {
		return !!(error || toggleHasError);
	}, [error, toggleHasError]);

	const getErrorType = (): ERROR_TYPE => (error ? ERROR_TYPE.FAILED : ERROR_TYPE.NO_DATA);

	// EvStackedBarChart can't handle string values, so we use a custom tooltip for the number formatting
	const customTooltip = useCallback(
		(context) => {
			const { y, name, point } = context;
			// Extract the date from the chartData at the corresponding index
			const dataPoint = chartData[point?.index || 0];
			const dateStr = dataPoint?.date || '';
			const tooltipContent = createTooltipContent(name, dateStr, y);

			return tooltipContent;
		},
		[chartData]
	);

	const gridYValuesTickInterval = useMemo(() => {
		if (!chartData?.length) {
			return undefined;
		}

		const maxTotal = Math.max(...chartData.map((item) => item.total || 0));

		if (maxTotal < CHART_CONSTANTS.WHOLE_NUMBER_THRESHOLD) {
			return 1;
		}

		return undefined;
	}, [chartData]);

	const customYaxisLabels = useCallback((value: number) => {
		if (value >= 1000) {
			return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}K`;
		}
		return value.toString();
	}, []);

	const chartClassName = `accessibility-critical-issues-stacked-chart ${className}`.trim();
	const srSummaryData = useMemo(() => {
		const labelCounts = new Map<string, number>();
		return chartData.map((item) => {
			const baseLabel = item.date?.trim() || 'Unknown date';
			const seen = labelCounts.get(baseLabel) ?? 0;
			labelCounts.set(baseLabel, seen + 1);
			const uniqueLabel = seen > 0 ? `${baseLabel} (${seen + 1})` : baseLabel;
			return {
				id: uniqueLabel,
				label: uniqueLabel,
				total: item.allIssuesTotal
			};
		});
	}, [chartData]);

	if (isLoading) {
		return (
			<EvSection className={chartClassName}>
				<div className="accessibility-critical-issues-stacked-chart-wrapper">
					<ChartSkeleton />
				</div>
			</EvSection>
		);
	}

	return (
		<EvSection className={chartClassName}>
			<div className="accessibility-critical-issues-stacked-chart-wrapper">
				<EvStackedBarChart
					title={title}
					data={chartData}
					patterns={patterns}
					height={height}
					customTooltip={customTooltip}
					customYaxisLabels={customYaxisLabels}
					gridYValuesTickInterval={gridYValuesTickInterval}
					srSummaryData={srSummaryData}
					className="accessibility-critical-issues-stacked-chart-inner"
					withLegend={true}
					withFiltering={true}
					excludeFields={CHART_CONSTANTS.EXCLUDED_FIELDS}
					headingLevel={CHART_CONSTANTS.HEADING_LEVEL}
					yAxisTitle={CHART_CONSTANTS.Y_AXIS_TITLE}
					isError={hasError}
					renderErrorState={() => <ErrorView errorType={getErrorType()} />}
					xAxis={{
						type: 'category' as const,
						categories,
						tickPositions: createStrategicTickPositions(categories.length)
					}}
					{...(canToggle && {
						dataToggle: {
							options: [CHART_TEXT.ISSUES_TOGGLE.UNIQUE, CHART_TEXT.ISSUES_TOGGLE.ALL],
							checked: showUnique,
							onChange: setShowUnique,
							ariaLabel: CHART_TEXT.ISSUES_TOGGLE.ARIA_LABEL
						}
					})}
					{...(showTable && {
						tableProps: {
							dataType: 'Critical Issues Stacked Data',
							columnsPattern: [
								{ name: CHART_TEXT.TABLE_COLUMNS.DATE, field: 'date', sort: true },
								{ name: CHART_TEXT.TABLE_COLUMNS.CRITICAL, field: 'critical', sort: true },
								{ name: CHART_TEXT.TABLE_COLUMNS.SERIOUS, field: 'serious', sort: true },
								{ name: CHART_TEXT.TABLE_COLUMNS.TOTAL, field: 'allIssuesTotal', sort: true }
							],
							defaultSortByField: 'date'
						}
					})}
				/>
			</div>
		</EvSection>
	);
};
